import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import * as PublicEvent from '../utils/activeEvent';
import './PublicMorningLoadOrder.css';

/** Default labels for plane ids (matches admin LoadOrder). */
const PLANE_LABELS = {
  A: 'Plane A',
  B: 'Plane B',
};

const DEFAULT_PLANE_ORDER = ['A', 'B'];

function planeLabel(planeId) {
  if (!planeId) return '—';
  return PLANE_LABELS[planeId] || `Plane ${planeId}`;
}

function PublicMorningLoadOrder() {
  const [searchParams] = useSearchParams();
  const eventIdParam = searchParams.get('event');
  const [activeEvent, setActiveEvent] = useState(null);
  const [bootstrapDone, setBootstrapDone] = useState(false);
  const [teams, setTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setError('');
      try {
        if (eventIdParam) {
          const snap = await getDoc(doc(firestore, 'events', eventIdParam));
          if (cancelled) return;
          if (snap.exists()) {
            setActiveEvent({ id: snap.id, ...snap.data() });
          } else {
            setError('Requested event was not found.');
          }
        } else {
          const id = await PublicEvent.getPublicDefaultEventId();
          if (!id) {
            if (!cancelled) setError('No public default event is configured.');
            return;
          }
          const snap = await getDoc(doc(firestore, 'events', id));
          if (cancelled) return;
          setActiveEvent(snap.exists() ? { id: snap.id, ...snap.data() } : null);
          if (!snap.exists()) setError('No public default event is configured.');
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load active event.');
      } finally {
        if (!cancelled) setBootstrapDone(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventIdParam]);

  const eventId = activeEvent?.id;

  useEffect(() => {
    if (!eventId) {
      setTeams([]);
      return;
    }
    let cancelled = false;
    setTeamsLoading(true);
    setError('');
    (async () => {
      try {
        const teamsSnap = await getDocs(query(collection(firestore, 'teams'), where('eventId', '==', eventId)));
        if (cancelled) return;
        setTeams(teamsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load morning load order.');
      } finally {
        if (!cancelled) setTeamsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const rows = useMemo(() => {
    const planeIndex = new Map(DEFAULT_PLANE_ORDER.map((id, idx) => [id, idx]));
    const assigned = teams.filter(
      (t) =>
        t.planeId &&
        t.planeLoadIndex != null &&
        t.slotIndexInLoad != null
    );

    const sorted = [...assigned].sort((a, b) => {
      if (a.planeLoadIndex !== b.planeLoadIndex) {
        return a.planeLoadIndex - b.planeLoadIndex;
      }
      const ai = planeIndex.get(a.planeId) ?? 999;
      const bi = planeIndex.get(b.planeId) ?? 999;
      if (ai !== bi) return ai - bi;
      if (a.planeId !== b.planeId) {
        return String(a.planeId).localeCompare(String(b.planeId));
      }
      return a.slotIndexInLoad - b.slotIndexInLoad;
    });

    return sorted.map((team) => ({
      id: team.id,
      planeId: team.planeId,
      planeLabel: planeLabel(team.planeId),
      loadIndex: team.planeLoadIndex,
      teamNumber: team.teamNumber || '?',
      mentor: team.mentorDisplayName || 'Unknown',
      freshlings: Array.isArray(team.freshlingsInfo) && team.freshlingsInfo.length > 0
        ? team.freshlingsInfo.map((fi) => fi.displayName || '').filter(Boolean).join(', ')
        : '—',
      flagged: Array.isArray(team.manifestFlagged) ? team.manifestFlagged : [],
    }));
  }, [teams]);

  return (
    <div className="public-morning-load-order">
      <nav className="public-morning-load-order-nav" aria-label="Page">
        <Link className="public-morning-load-order-back" to="/team-dashboard">
          ← Return to dashboard
        </Link>
      </nav>
      <header className="public-morning-load-order-header">
        <h1>Morning Load Order</h1>
        <p className="public-morning-load-order-subtitle">
          {activeEvent?.title || '—'}
        </p>
      </header>

      <section className="public-morning-load-order-day-info" aria-label="Competition day information">
        <ul>
          <li>
            <strong>Four jumps:</strong> the Saturday competition includes four jumps.
          </li>
          <li>
            <strong>Ground time:</strong> plan for a <strong>minimum of one hour on the ground</strong> between jumps.
          </li>
          <li>
            <strong>End of day:</strong> competition ends at <strong>5:00pm</strong>, when <strong>awards begin</strong>.{' '}
            <Link className="public-morning-load-order-schedule-link" to="/schedule">
              Full schedule
            </Link>
          </li>
        </ul>
      </section>

      <section className="public-morning-load-order-disclaimer">
        <strong>Preliminary list:</strong> this load order may still change. Please check the final list in the morning and the manifest board.
      </section>

      {error && <p className="public-morning-load-order-error">{error}</p>}
      {!bootstrapDone ? (
        <p>Loading…</p>
      ) : !activeEvent ? (
        <p>No active event.</p>
      ) : teamsLoading ? (
        <p>Loading…</p>
      ) : rows.length === 0 ? (
        <p>No assigned teams in the current load order yet.</p>
      ) : (
        <ul className="public-morning-load-order-list">
          {rows.map((row) => (
            <li
              key={row.id}
              className={
                'public-morning-load-order-line' +
                (row.planeId === 'A'
                  ? ' public-morning-load-order-line--plane-a'
                  : row.planeId === 'B'
                    ? ' public-morning-load-order-line--plane-b'
                    : ' public-morning-load-order-line--plane-other') +
                (row.flagged.length ? ' public-morning-load-order-line--flagged' : '')
              }
            >
              <span className="public-morning-load-order-line-inner">
                <span className="public-morning-load-order-team">Team {row.teamNumber}</span>
                <span className="public-morning-load-order-sep" aria-hidden="true">
                  ·
                </span>
                <span>{row.planeLabel}</span>
                <span className="public-morning-load-order-sep" aria-hidden="true">
                  ·
                </span>
                <span>Load {row.loadIndex}</span>
                <span className="public-morning-load-order-sep" aria-hidden="true">
                  ·
                </span>
                <span>
                  <strong>Mentor:</strong> {row.mentor}
                </span>
                <span className="public-morning-load-order-sep" aria-hidden="true">
                  ·
                </span>
                <span>
                  <strong>Freshlings:</strong> {row.freshlings}
                </span>
                {row.flagged.length > 0 && (
                  <>
                    <span className="public-morning-load-order-sep" aria-hidden="true">
                      ·
                    </span>
                    <span className="public-morning-load-order-flag-inline">
                      Manifest check-in: {row.flagged.join(', ')}
                    </span>
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default PublicMorningLoadOrder;
